import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Loader2, MailCheck } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useNoIndex } from "../lib/useNoIndex";
import { EmSwash } from "../components/EmSwash";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  useNoIndex();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(Array.isArray(d) ? "Please enter a valid email address." : (d || "Something went wrong. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="forgot-page" className="bg-sand">
      <Navbar onStart={() => navigate("/register")} onDonate={() => navigate("/login")} primaryLabel="Create account" secondaryLabel="Sign in" />
      <section className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 pb-24 pt-32 lg:grid-cols-2 lg:gap-24 lg:px-8 lg:pt-44">
        <div>
          <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">Password reset</span>
          <h1 className="mt-4 font-serif text-3xl lg:text-[3.5rem] leading-[0.95] tracking-tight text-ink">
            Forgot it? <EmSwash>Happens.</EmSwash>
          </h1>
          <p className="mt-6 max-w-md text-base leading-relaxed text-olive">
            Enter the email on your student account and we&rsquo;ll send a link to choose a new password. The link works once and expires in 60 minutes.
          </p>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          onSubmit={submit}
          className="rounded-2xl border-2 border-tangerine bg-tangerine/[0.04] p-6 lg:p-8"
          data-testid="forgot-form"
        >
          {sent ? (
            <div data-testid="forgot-sent">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-grass/15 text-grass"><MailCheck className="h-6 w-6" strokeWidth={2} /></span>
              <h2 className="mt-4 font-serif text-3xl text-ink">Check your inbox.</h2>
              <p className="mt-3 text-base leading-relaxed text-olive">
                If an account exists for <strong className="text-ink">{email.trim()}</strong>, a reset link is on its way. It expires in 60 minutes. Not there in a minute or two? Check spam, or make sure you used the address you signed up with.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button type="button" onClick={() => { setSent(false); }} data-testid="forgot-again" className="rounded-full border border-ink px-5 py-2.5 text-base font-semibold text-ink hover:bg-ink hover:text-sand">Use a different email</button>
                <Link to="/login" data-testid="forgot-back" className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-base font-semibold text-sand hover:bg-forest">Back to sign in <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </div>
          ) : (
            <>
              <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">For students</span>
              <h2 className="mt-3 font-serif text-3xl text-ink">Send me a reset link.</h2>
              <label className="mt-6 block">
                <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">Email</span>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                  className="mt-2 w-full rounded-md border border-border bg-sand px-4 py-3 font-serif text-3xl text-ink focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10"
                />
              </label>
              {error && <p role="alert" data-testid="forgot-error" className="mt-4 text-[13px] text-scarlet">{error}</p>}
              <button type="submit" disabled={submitting} data-testid="forgot-submit" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-5 py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-60 sm:w-auto">
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Email me a link <ArrowRight className="h-4 w-4" /></>}
              </button>
              <p className="mt-5 text-[13px] text-olive">
                Remembered it? <Link to="/login" data-testid="forgot-login-link" className="font-semibold text-ink underline underline-offset-4 hover:text-tangerine">Back to sign in</Link>
                <span className="mx-2 text-border">·</span>
                Donors sign in with Google — no password to reset.
              </p>
            </>
          )}
        </motion.form>
      </section>
      <Footer variant="minimal" />
    </main>
  );
}
